import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/apiService';
import { tagsOf } from '../../lib/memoryTags';
import { ICheck } from './DashIcons';

const WINDOW_DAYS = 14;
// Pull a generous slice so the home page reflects real corpus size for
// most users. The API returns `count = len(results)` (page size, not DB
// total), so the displayed count caps at this limit until we ship a
// dedicated /memory/count endpoint.
const HOME_FETCH_LIMIT = 10000;

// Short first name / initial for the greeting — falls back gracefully
// when the auth record only has an email.
const firstName = (user) => {
  if (!user) return 'there';
  if (user.name) return user.name.split(' ')[0];
  if (user.email) return user.email.split('@')[0];
  return 'there';
};

const timeAgoLong = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const DashboardHomePage = () => {
  const { user } = useAuth();
  const userKey = user?.userId || user?.id || 'anon';

  const { data: memoriesData, isLoading } = useQuery({
    queryKey: ['memories-home', userKey],
    queryFn: () => apiService.getRecentMemories({ limit: HOME_FETCH_LIMIT }),
    staleTime: 60000,
    enabled: !!user,
  });

  // Real DB total — independent of how many we fetched in `memoriesData`.
  // When this resolves, the KPI shows the true number (no "+10000" cap).
  // Falls back to the loaded page size if /memory/count is unavailable
  // (older deploys).
  const { data: countData } = useQuery({
    queryKey: ['memories-count', userKey],
    queryFn: () => apiService.getMemoryCount(),
    staleTime: 60000,
    enabled: !!user,
  });

  // Service-wide metrics (no auth, global counters from prom-style /metrics).
  // Drives the "Avg inject" KPI which is system-wide latency, NOT
  // per-user. Label reflects that.
  const { data: metricsData } = useQuery({
    queryKey: ['tagging-metrics'],
    queryFn: () => apiService.getTaggingMetrics(),
    staleTime: 30000,
    refetchInterval: 60000,
    enabled: !!user,
  });

  // Pack acceptance over the last 30 days — real aggregation from
  // /events/pack/aggregate. Replaces the old hardcoded 73% mock.
  // When no events exist yet (new orgs), the KPI shows "—" rather
  // than 0%, since 0% would imply rejection rather than absence.
  const { data: packAgg } = useQuery({
    queryKey: ['pack-aggregate', userKey, 720],
    queryFn: () => apiService.getPackAggregate({ hours: 720 }),
    staleTime: 60000,
    enabled: !!user,
  });

  const allMemories = useMemo(() => {
    const raw =
      memoriesData?.results ||
      memoriesData?.data?.blocks ||
      memoriesData?.blocks ||
      memoriesData?.data ||
      [];
    return Array.isArray(raw) ? raw : [];
  }, [memoriesData]);

  // Prefer the real DB total from /memory/count; fall back to the loaded
  // page size while it loads (or if the endpoint isn't available yet on
  // a stale prod). The "+" suffix is only shown when we genuinely don't
  // know the true total — i.e. the count endpoint failed and we're at
  // the page-fetch ceiling.
  const loadedCount = allMemories.length;
  const realCount = typeof countData?.count === 'number' ? countData.count : null;
  const displayCount = realCount ?? loadedCount;
  const isCountTruncated = realCount === null && loadedCount >= HOME_FETCH_LIMIT;

  // Active tags — unique tag count across all loaded memories.
  const activeTags = useMemo(() => {
    const set = new Set();
    allMemories.forEach((m) => {
      tagsOf(m).forEach((name) => set.add(name));
    });
    return set.size;
  }, [allMemories]);

  // This-week memory count — used in the "↑ N this week" delta.
  const weekCount = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400 * 1000;
    return allMemories.filter((m) => {
      const ts = m.created_at || m.createdAt || m.timestamp;
      const t = ts ? new Date(ts).getTime() : NaN;
      return Number.isFinite(t) && t >= weekAgo;
    }).length;
  }, [allMemories]);

  // 14-day memory buckets for the sparkline. Uses date-only keys so
  // memories saved at different hours of the same day roll up cleanly.
  const series = useMemo(() => {
    const buckets = new Array(WINDOW_DAYS).fill(0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startTs = today.getTime() - (WINDOW_DAYS - 1) * 86400 * 1000;
    allMemories.forEach((m) => {
      const raw = m.created_at || m.createdAt || m.timestamp;
      if (!raw) return;
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return;
      const day = new Date(t);
      day.setHours(0, 0, 0, 0);
      const idx = Math.floor((day.getTime() - startTs) / 86400000);
      if (idx >= 0 && idx < WINDOW_DAYS) buckets[idx] += 1;
    });
    return buckets;
  }, [allMemories]);

  // Chart geometry — asymmetric padding leaves room for the Y-axis value
  // labels on the left and a little headroom on top so the peak label
  // doesn't get clipped.
  // Tokens stored — char/4 heuristic across the loaded window. Honest
  // since UsagePage uses the same estimate, and the inject path uses
  // tiktoken when token counts actually matter.
  const tokenEstimate = (s) => (s ? Math.ceil(s.length / 4) : 0);
  const tokensStored = useMemo(
    () => allMemories.reduce((acc, m) => acc + (m.tokens || m.tokenCount || tokenEstimate(m.content || '')), 0),
    [allMemories],
  );
  const tokensDisplay = useMemo(() => {
    if (tokensStored >= 1_000_000) return `${(tokensStored / 1_000_000).toFixed(1)}M`;
    if (tokensStored >= 1_000) return `${(tokensStored / 1_000).toFixed(1)}k`;
    return String(tokensStored);
  }, [tokensStored]);

  // Inject latency from service metrics. The /metrics endpoint now
  // returns p50/p95/p99 estimated from a cumulative histogram of
  // observed durations. sum/count is the avg fallback when no
  // observations exist yet. Values are in seconds; convert to ms.
  const injectAvgMs = useMemo(() => {
    const s = metricsData?.inject_duration?.success;
    if (!s || !s.count || s.count === 0) return null;
    return Math.round((s.sum / s.count) * 1000);
  }, [metricsData]);
  const injectP95Ms = useMemo(() => {
    const s = metricsData?.inject_duration?.success;
    if (!s || !s.count || s.count === 0 || !s.p95) return null;
    return Math.round(s.p95 * 1000);
  }, [metricsData]);

  // Pack accept rate as a percentage; null when there are no events yet.
  const acceptRatePct = useMemo(() => {
    if (!packAgg || !packAgg.total) return null;
    return Math.round(packAgg.acceptRate * 100);
  }, [packAgg]);

  const seriesMax = Math.max(1, ...series);
  const seriesTotal = series.reduce((a, b) => a + b, 0);
  const w = 640;
  const h = 200;
  const padLeft = 32;
  const padRight = 12;
  const padTop = 14;
  const padBottom = 22;
  const plotW = w - padLeft - padRight;
  const plotH = h - padTop - padBottom;
  const xs = series.map((_, i) => padLeft + (i / (series.length - 1)) * plotW);
  const ys = series.map((v) => padTop + plotH - (v / seriesMax) * plotH);
  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  const fillPath = `${linePath} L${xs[xs.length - 1]},${padTop + plotH} L${xs[0]},${padTop + plotH} Z`;
  // Y-axis ticks: 0 / mid / max. seriesMax is always >= 1 so labels never
  // overlap each other. mid is rounded to keep numbers readable.
  const yTicks = [
    { v: 0, y: padTop + plotH },
    { v: Math.round(seriesMax / 2), y: padTop + plotH / 2 },
    { v: seriesMax, y: padTop },
  ];

  // Tag activation counts across the loaded window — surfaces the same
  // "hot" tags the filter pills use on the Memories page.
  const hotTags = useMemo(() => {
    const counts = {};
    allMemories.forEach((m) => {
      tagsOf(m).forEach((name) => {
        counts[name] = (counts[name] || 0) + 1;
      });
    });
    const rows = Object.entries(counts).map(([t, c]) => ({ t, c }));
    rows.sort((a, b) => b.c - a.c);
    const top = rows.slice(0, 6);
    const max = Math.max(1, ...top.map((r) => r.c));
    return top.map((r) => ({ ...r, w: r.c / max }));
  }, [allMemories]);

  // Recent activity — 4 most recent memories, rendered as a "pack-like" row.
  const recentRows = useMemo(() => {
    const sorted = [...allMemories].sort((a, b) => {
      const ta = new Date(a.created_at || a.createdAt || a.timestamp || 0).getTime();
      const tb = new Date(b.created_at || b.createdAt || b.timestamp || 0).getTime();
      return tb - ta;
    });
    return sorted.slice(0, 4);
  }, [allMemories]);

  const windowStart = new Date(Date.now() - (WINDOW_DAYS - 1) * 86400 * 1000);
  const windowMid = new Date(Date.now() - Math.floor((WINDOW_DAYS - 1) / 2) * 86400 * 1000);
  const formatDay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  // Compact day-of-month for per-point labels under the x-axis.
  const dayLabel = (i) => {
    const d = new Date(Date.now() - (WINDOW_DAYS - 1 - i) * 86400 * 1000);
    return d.getDate();
  };

  return (
    <>
      <Helmet><title>Home · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>
            Welcome back, <em>{firstName(user)}</em>.
          </h1>
          <div className="sub">
            {isLoading
              ? 'Loading your memory workspace…'
              : `Your agent has saved ${displayCount.toLocaleString()}${isCountTruncated ? '+' : ''} memories so far. The tag-graph is quietly getting smarter.`}
          </div>
        </div>

        <div className="kpi-row">
          <div className="kpi">
            <div className="lbl">Total memories</div>
            <div className="val">
              {isLoading ? '—' : displayCount.toLocaleString()}
              {!isLoading && isCountTruncated && <em>+</em>}
            </div>
            <div className={`delta ${weekCount > 0 ? 'up' : ''}`}>
              {isLoading
                ? ' '
                : weekCount > 0
                ? `↑ ${weekCount} this week`
                : 'no new this week'}
            </div>
          </div>
          <div className="kpi">
            <div className="lbl">Active tags</div>
            <div className="val">{isLoading ? '—' : activeTags.toLocaleString()}</div>
            <div className="delta">M=32 · D=2</div>
          </div>
          <div className="kpi">
            <div className="lbl">Tokens stored</div>
            <div className="val">{isLoading ? '—' : tokensDisplay}</div>
            <div className="delta">
              {isLoading
                ? ' '
                : `across ${loadedCount.toLocaleString()} loaded`}
            </div>
          </div>
          <div className="kpi">
            <div className="lbl">Pack acceptance</div>
            <div className="val">
              {acceptRatePct == null ? '—' : <>{acceptRatePct}<em>%</em></>}
            </div>
            <div className="delta">
              {packAgg?.total
                ? `${packAgg.total.toLocaleString()} events · 30d`
                : injectP95Ms != null
                ? `inject P95 ${injectP95Ms}ms`
                : injectAvgMs != null
                ? `inject avg ${injectAvgMs}ms`
                : 'no feedback yet · 30d'}
            </div>
          </div>
        </div>

        <div className="two-col">
          <div className="panel">
            <div className="panel-head">
              <h3>Memory activity</h3>
              <span className="meta">
                last {WINDOW_DAYS} days · {seriesTotal} saved · peak {seriesMax}/day
              </span>
            </div>
            <div className="panel-body">
              <svg
                className="chart"
                viewBox={`0 0 ${w} ${h}`}
                preserveAspectRatio="none"
                role="img"
                aria-label={`Memory saves per day, last ${WINDOW_DAYS} days. Total ${seriesTotal}, peak ${seriesMax} on a single day.`}
              >
                <defs>
                  <linearGradient id="sparkGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.72 0.14 60)" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="oklch(0.72 0.14 60)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {/* Y-axis gridlines + numeric labels */}
                {yTicks.map((tick) => (
                  <g key={tick.v}>
                    <line
                      x1={padLeft}
                      x2={w - padRight}
                      y1={tick.y}
                      y2={tick.y}
                      stroke="#E3E2DC"
                      strokeDasharray="2 4"
                    />
                    <text
                      x={padLeft - 6}
                      y={tick.y + 3}
                      textAnchor="end"
                      fontFamily="var(--mono)"
                      fontSize="10"
                      fill="var(--ink-4)"
                    >
                      {tick.v}
                    </text>
                  </g>
                ))}
                {/* Series area + line */}
                <path d={fillPath} fill="url(#sparkGrad)" />
                <path d={linePath} fill="none" stroke="#0A0A0A" strokeWidth="1.5" />
                {/* Data points + per-point value labels (only when v > 0) */}
                {xs.map((x, i) => (
                  <g key={i}>
                    <circle cx={x} cy={ys[i]} r="2.5" fill="#0A0A0A" />
                    {series[i] > 0 && (
                      <text
                        x={x}
                        y={ys[i] - 6}
                        textAnchor="middle"
                        fontFamily="var(--mono)"
                        fontSize="9"
                        fill="var(--ink-3)"
                      >
                        {series[i]}
                      </text>
                    )}
                    {/* Compact day-of-month under each point */}
                    <text
                      x={x}
                      y={h - 6}
                      textAnchor="middle"
                      fontFamily="var(--mono)"
                      fontSize="9"
                      fill="var(--ink-4)"
                    >
                      {dayLabel(i)}
                    </text>
                  </g>
                ))}
              </svg>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 6,
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  color: 'var(--ink-4)',
                }}
              >
                <span>{formatDay(windowStart)}</span>
                <span>{formatDay(windowMid)}</span>
                <span>today</span>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3>Hot tags</h3>
              <span className="meta">by activation</span>
            </div>
            <div className="panel-body">
              {hotTags.length === 0 ? (
                <div className="empty" style={{ padding: '28px 0' }}>
                  {isLoading ? 'Loading tags…' : 'No tags yet — save a memory to get started.'}
                </div>
              ) : (
                hotTags.map((row) => (
                  <div key={row.t} className="hot-row">
                    <span className="t" title={row.t}>{row.t}</span>
                    <div className="bar-wrap">
                      <div style={{ width: `${row.w * 100}%` }} />
                    </div>
                    <span className="c">{row.c}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Recent memories</h3>
            <span className="meta">live feed</span>
          </div>
          {recentRows.length === 0 ? (
            <div className="empty">
              {isLoading ? 'Loading…' : 'No memories yet — connect an MCP client and your first one will appear here.'}
            </div>
          ) : (
            <table className="mtable">
              <thead>
                <tr>
                  <th>Content</th>
                  <th style={{ width: 260 }}>Tags</th>
                  <th style={{ width: 120 }}>Tokens</th>
                  <th style={{ width: 110 }}>Verdict</th>
                  <th style={{ width: 100 }}>When</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map((m, i) => {
                  const id = m.id || m._id || `row-${i}`;
                  const content = (m.content || '').slice(0, 100);
                  const tags = tagsOf(m);
                  const hotCount = tags.slice(0, 3);
                  const tokens = m.tokens || m.tokenCount || Math.ceil((m.content || '').length / 4);
                  const ts = m.created_at || m.createdAt || m.timestamp;
                  return (
                    <tr key={id}>
                      <td style={{ maxWidth: 480 }}>{content || '—'}</td>
                      <td>
                        {hotCount.length === 0 ? (
                          <span className="tag">untagged</span>
                        ) : (
                          hotCount.map((t) => (
                            <span key={t} className="tag">
                              {t}
                            </span>
                          ))
                        )}
                        {tags.length > 3 && (
                          <span className="tag">+{tags.length - 3}</span>
                        )}
                      </td>
                      <td>
                        <span className="score">
                          {tokens}t
                        </span>
                      </td>
                      <td>
                        <span className="verdict ok">
                          <ICheck size={12} /> saved
                        </span>
                      </td>
                      <td className="ts">{timeAgoLong(ts)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardHomePage;
